import { useMemo } from 'react'

import { Chip } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { useDate, useMoney } from '@circlefin/hooks'
import { TradeStatus } from '@shared/graphql'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import type { Locale } from '@circlefin/money/variants'
import type { Trade } from '@shared/graphql'

const textClassnames = 'text-sm leading-5 text-black-600'

const amountClassnames = 'whitespace-normal w-20 truncate'

const columnHelper = createColumnHelper<Trade>()

export const useTradesTableColumns = () => {
  const { t } = useTranslation('payments')
  const { locale } = useRouter()
  const { money } = useMoney({ locale: locale as Locale })
  const { date: formatDate } = useDate({ locale: locale as Locale })

  return useMemo(() => {
    const createDate = columnHelper.accessor('createDate', {
      header: t<string>`tradesTable.headings.createdDate`,
      meta: {
        className: 'w-10',
      },
      cell: ({ getValue }) => (
        <span className={textClassnames}>{formatDate(getValue(), 'date')}</span>
      ),
    })

    const tradeId = columnHelper.accessor('id', {
      header: t<string>`tradesTable.headings.tradeId`,
      meta: {
        className: 'w-35',
      },
      cell: ({ getValue }) => (
        <span className={textClassnames}>{getValue()}</span>
      ),
    })

    const fromAmount = columnHelper.accessor('from', {
      header: t<string>`tradesTable.headings.fromAmount`,
      meta: {
        className: amountClassnames,
      },
      cell: ({ getValue }) => {
        const value = getValue()

        return (
          <span className={classNames('font-circular-medium', textClassnames)}>
            {value.amount && value.currency != null
              ? money({
                  number: Number(value.amount),
                  variant: value.currency,
                  options: {
                    symbol: true,
                    signDisplay: false,
                    padded: false,
                  },
                })
              : '—'}
          </span>
        )
      },
    })

    const toAmount = columnHelper.accessor('to', {
      header: t<string>`tradesTable.headings.toAmount`,
      meta: {
        className: amountClassnames,
      },
      cell: ({ getValue }) => {
        const value = getValue()

        return (
          <span className={classNames('font-circular-medium', textClassnames)}>
            {value.amount && value.currency != null
              ? money({
                  number: Number(value.amount),
                  variant: value.currency,
                  options: {
                    symbol: true,
                    signDisplay: false,
                    padded: false,
                  },
                })
              : '—'}
          </span>
        )
      },
    })

    const status = columnHelper.accessor('status', {
      header: t<string>`tradesTable.headings.status`,
      meta: {
        className: 'w-15',
      },
      cell: ({ getValue }) => {
        const value = getValue()

        const getStatusVariant = (status: TradeStatus) => {
          switch (status) {
            case TradeStatus.complete:
              return 'status/success'
            case TradeStatus.failed:
              return 'status/error'
            case TradeStatus.pending:
            case TradeStatus.pending_settlement:
            case TradeStatus.confirmed:
              return 'status/neutral'
          }
        }

        return (
          <div className="flex items-center justify-between">
            <Chip variant={getStatusVariant(value)}>
              <span className="capitalize">{value}</span>
            </Chip>
          </div>
        )
      },
    })

    return [createDate, tradeId, fromAmount, toAmount, status]
  }, [money, formatDate, t])
}
